import { FocusEventHandler, memo } from 'react';
import classnames from 'classnames';
import noop from 'no-op';

import styles from './VideoControls.module.scss';

import BaseButton from '../../BaseButton/BaseButton';
import CaptionsOffIcon from './assets/captions-off.svg';
import CaptionsOnIcon from './assets/captions-on.svg';
import MutedIcon from './assets/muted.svg';
import PauseIcon from './assets/pause.svg';
import PlayIcon from './assets/play.svg';
import UnmutedIcon from './assets/unmuted.svg';

export type Props = {
  className?: string;
  duration: number;
  currentTime: number;
  onPlayToggle?: Function;
  isPlaying?: boolean;
  onTimeUpdate?: Function;
  isMuted?: boolean;
  onMuteToggle?: Function;
  navAriaLabel?: string;
  playLabel?: string;
  pauseLabel?: string;
  unmuteLabel?: string;
  muteLabel?: string;
  onFocus: Function;
  onBlur: Function;
  captions?: boolean;
  captionsHideLabel?: string;
  captionsShowLabel?: string;
  isShowingCaptions?: boolean;
  onCaptionsToggle?: Function;
};

const VideoControls = ({
  className,
  onPlayToggle = noop,
  isPlaying,
  isMuted,
  onMuteToggle = noop,
  navAriaLabel = 'Video Controls',
  playLabel = 'Play Video',
  pauseLabel = 'Pause Video',
  unmuteLabel = 'Unmute Video',
  muteLabel = 'Mute Video',
  onFocus = noop,
  onBlur = noop,
  captions,
  captionsHideLabel = 'Hide Captions',
  captionsShowLabel = 'Show Captions',
  onCaptionsToggle = noop,
  isShowingCaptions
}: // eslint-disable-next-line sonarjs/cognitive-complexity
Props) => {
  return (
    <nav
      className={classnames(styles.VideoControls, className, 'video-controls')}
      aria-label={navAriaLabel}
      onFocus={onFocus as FocusEventHandler}
      onBlur={onBlur as FocusEventHandler}
    >
      <BaseButton
        className={styles.button}
        aria-label={isPlaying ? pauseLabel : playLabel}
        title={isPlaying ? pauseLabel : playLabel}
        onClick={onPlayToggle}
      >
        {isPlaying ? <PauseIcon aria-hidden /> : <PlayIcon aria-hidden />}
      </BaseButton>

      <BaseButton
        className={styles.button}
        aria-label={isMuted ? unmuteLabel : muteLabel}
        title={isMuted ? unmuteLabel : muteLabel}
        onClick={onMuteToggle}
      >
        {isMuted ? <MutedIcon aria-hidden /> : <UnmutedIcon aria-hidden />}
      </BaseButton>

      {captions && (
        <BaseButton
          className={classnames(styles.button, styles.captions)}
          aria-label={isShowingCaptions ? captionsHideLabel : captionsShowLabel}
          title={isShowingCaptions ? captionsHideLabel : captionsShowLabel}
          onClick={onCaptionsToggle}
        >
          {isShowingCaptions ? <CaptionsOnIcon aria-hidden /> : <CaptionsOffIcon aria-hidden />}
        </BaseButton>
      )}
    </nav>
  );
};

export default memo(VideoControls);
